import { toast } from 'sonner';

const PERMISSION_PROMPTED_KEY = 'commsNotificationsPermissionPrompted';

export function isCommsNotificationSupported() {
  return typeof Notification !== 'undefined';
}

export function getCommsNotificationPermission() {
  if (!isCommsNotificationSupported()) return 'unsupported';
  return Notification.permission;
}

/** @deprecated Use getCommsNotificationPermission */
export const getVoiceNotificationPermission = getCommsNotificationPermission;

/** @deprecated Use isCommsNotificationSupported */
export const isVoiceNotificationSupported = isCommsNotificationSupported;

/**
 * Prompt once per browser session (after a user gesture when possible).
 * Safe to call from outbound call, voice device setup, or first pointer interaction.
 */
export async function ensureCommsNotificationPermission() {
  if (!isCommsNotificationSupported()) return 'unsupported';
  if (Notification.permission === 'granted') return 'granted';
  if (Notification.permission === 'denied') return 'denied';

  try {
    if (sessionStorage.getItem(PERMISSION_PROMPTED_KEY) === '1') {
      return Notification.permission;
    }
    sessionStorage.setItem(PERMISSION_PROMPTED_KEY, '1');
    return await Notification.requestPermission();
  } catch {
    return Notification.permission;
  }
}

/** @deprecated Use ensureCommsNotificationPermission */
export const ensureVoiceNotificationPermission = ensureCommsNotificationPermission;

function showOsNotification({ title, body, tag }) {
  if (!isCommsNotificationSupported() || Notification.permission !== 'granted') return;

  try {
    const notification = new Notification(title, {
      body,
      tag,
      renotify: true,
    });
    notification.onclick = () => {
      window.focus();
      notification.close();
    };
  } catch {
    // ignore blocked or unsupported notifications
  }
}

function notifyOsOnly({ title, osBody, tag }) {
  if (Notification.permission !== 'granted') return;
  showOsNotification({ title, body: osBody, tag });
}

function notifyWithToastAndOs({
  title,
  toastDescription,
  osBody,
  tag,
  osWhenFocused = false,
}) {
  toast.info(title, {
    description: toastDescription,
    duration: 5000,
  });

  const hidden = typeof document !== 'undefined' && document.visibilityState === 'hidden';
  if (Notification.permission === 'granted' && (hidden || osWhenFocused)) {
    showOsNotification({ title, body: osBody, tag });
  }
}

export function notifyCallEnded({ clientName } = {}) {
  const body = clientName ? `Call with ${clientName} has ended.` : 'Your phone call has ended.';
  notifyOsOnly({
    title: 'Call ended',
    osBody: body,
    tag: 'voice-call-ended',
  });
}

export function notifyIncomingCall({ clientName, callerPhone } = {}) {
  const label = clientName || 'Client';
  const phoneSuffix = callerPhone ? ` (${callerPhone})` : '';
  notifyOsOnly({
    title: 'Incoming call',
    osBody: `${label}${phoneSuffix} is calling.`,
    tag: 'voice-call-incoming',
  });
}

export function notifyInboundSms({ clientName, preview, clientId } = {}) {
  const label = clientName || 'Client';
  const messagePreview = preview || 'New message';
  notifyWithToastAndOs({
    title: `SMS from ${label}`,
    toastDescription: messagePreview,
    osBody: messagePreview,
    tag: clientId ? `sms-inbound-${clientId}` : 'sms-inbound',
    osWhenFocused: false,
  });
}
